import type { CardProps, CollapseProps } from '@packages/shared';
import { Card, CardActions, Collapse, Divider, Typography } from '@packages/shared';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Overlay } from './Overlay';
import { ExpandMoreIcon } from './ExpandMoreIcon';

export type CollapsibleCardProps = {
  /** Optional children react node */
  children?: ReactNode;
  /**
   * Children for the collapsed content.
   */
  collapseChildren: ReactNode;

  /**
   * The width (horizontal) or height (vertical) of the container when collapsed.
   * @default '0px'
   */
  collapsedSize?: CollapseProps['collapsedSize'];
  /**
   * Text that can be clicked to extend/collapse the card.
   */
  cardActionText: {
    /**
     * Text that should be shown to extend card
     */
    expandText: string;
    /**
     * Text that should be shown to collapse card
     */
    collapseText: string;
  };
} & CardProps;

/**
 * This Component is an extension of the Card component from MUI: https://mui.com/material-ui/react-card/
 *
 * In addition to the default Card, you can add collapseChildren, which can be expanded on click.
 * */
export const CollapsibleCard = ({
  cardActionText,
  children,
  collapseChildren,
  collapsedSize,
  ...rest
}: CollapsibleCardProps) => {
  const [expanded, setExpanded] = useState(false);

  const handleExpandClick = () => {
    setExpanded((prev) => !prev);
  };

  return (
    <Card {...rest}>
      {children}
      <Collapse collapsedSize={collapsedSize} in={expanded} sx={{ position: 'relative' }}>
        {!collapsedSize && <Divider />}
        {collapsedSize && <Overlay expanded={expanded} />}
        {collapseChildren}
      </Collapse>
      <Divider />
      <CardActions onClick={() => handleExpandClick()} sx={{ cursor: 'pointer', gap: 1 }}>
        <Typography variant="body2">
          {/** If card is expanded then it shows the text to collapse the card and vice versa */}
          {expanded ? cardActionText.collapseText : cardActionText.expandText}
        </Typography>
        <ExpandMoreIcon expanded={expanded} />
      </CardActions>
    </Card>
  );
};
